import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'

import { Feature, ProductIds } from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import getEELegalDocuments from 'core/application/employeeDashboard/legalDocuments/benefitPlanDocuments'
import { LegalDocument } from 'core/domain/legalDocuments/legalDocuments'

import Card from 'common/components/dataDisplay/Card'

import { useFeatureFlag, useLoginUserV2 } from 'common/hooks'
import { ProductConfig } from 'common/providers/ProductProvider'

import { BenefitPlanPeriodDropDown, Empty } from '../../../common/components'
import useQueryParams from '../../../common/hooks/useQueryParams'
import useQueryRequest from '../../../common/hooks/useQueryRequest'
import DocumentCard from './DocumentCard'

export const LEGAL_DOCUMENTS_PATH = 'legal-documents'

const LegalDocuments: React.FC = () => {
  const queryParams = useQueryParams()
  const { t: translate } = useTranslation()

  const [planPeriods] = useQueryRequest<PlanPeriodDto[]>(null, () => benefitPlanPeriods())
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([])
  const [, isLegalDocumentLoading, sendGetLegalDocuments] = useQueryRequest(null)

  const featureFlag = useFeatureFlag(Feature.ShowNonEligibleEmployeeLegalDocumentAlert)

  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  const { user } = useLoginUserV2()

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    ;(async () => {
      if (selectedPlanPeriod) {
        const data = await sendGetLegalDocuments(() => getEELegalDocuments(selectedPlanPeriod.id))

        let listOfLegalDocuments: LegalDocument[] = []
        data.forEach(
          (
            { documentType, planPeriodId, productClassId, title, description, fileUrl }: LegalDocument,
            index: number,
          ) => {
            listOfLegalDocuments.push({
              key: index + 1,
              documentType,
              productClassId,
              planPeriodId,
              title,
              description,
              fileUrl,
            })
          },
        )
        setLegalDocuments(listOfLegalDocuments)
      }
    })()
  }, [selectedPlanPeriod])

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  return (
    <Card
      title={
        window.innerWidth > 474 && (
          <h5 key="title" className="legal-documents-text-decoration">
            {translate('admin-dashboard.legal-documents.title')}
          </h5>
        )
      }
      className="legal_documents__summary"
      extra={
        <div key="summary" className="benefit_period">
          {planPeriods && (
            <BenefitPlanPeriodDropDown
              defaultValue={planPeriodIdQueryParam}
              planPeriods={planPeriods}
              onPlanPeriodSelected={onPlanPeriodChanged}
            />
          )}
        </div>
      }
    >
      <Skeleton active loading={isLegalDocumentLoading}>
        {featureFlag && legalDocuments.length === 0 ? (
          <Empty
            description={
              <>
                <h4>You are not currently eligible for the {ProductConfig.get(user.productId).productName}.</h4>
                <p>If you believe that this is in error, please contact your benefits administrator.</p>
              </>
            }
          />
        ) : (
          legalDocuments.map(({ documentType, planPeriodId, productClassId, title, description, fileUrl }) => (
            <Row>
              <Col span={24}>
                <DocumentCard
                  documentType={documentType}
                  productClassId={productClassId}
                  planPeriodId={planPeriodId}
                  title={title}
                  link={fileUrl}
                  linkName={translate('admin-dashboard.legal-documents.summary.view-document')}
                  description={description}
                />
              </Col>
            </Row>
          ))
        )}
      </Skeleton>
    </Card>
  )
}

export default LegalDocuments
